<script setup lang="ts">
import { computed } from 'vue'
import { ArrowRight } from '@lucide/vue'

const props = withDefaults(defineProps<{
  variant?: 'solid' | 'outline' | 'ghost'
  size?: 'sm' | 'md'
  as?: 'button' | 'a'
  href?: string
  target?: string
  rel?: string
  disabled?: boolean
  icon?: boolean
}>(), {
  variant: 'solid',
  size: 'md',
  as: 'button',
  disabled: false,
  icon: false,
})

defineEmits<{ click: [e: MouseEvent] }>()

const tag = computed(() => props.href ? 'a' : props.as)

const classes = computed(() => {
  const base = 'inline-flex items-center justify-center gap-1.5 font-normal whitespace-nowrap cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:pointer-events-none disabled:opacity-50'

  const sizes = {
    sm: 'h-8 px-3.5 text-body-sm md:h-9 md:px-5 md:text-body-lg rounded-md',
    md: 'h-11 px-6 text-btn-lg rounded-md',
  }

  const variants = {
    // filled primary blue button
    solid: 'bg-primary text-white shadow-button hover:bg-primary-dark',
    // bordered primary button
    outline: 'border border-primary text-primary bg-transparent hover:bg-primary hover:text-white',
    // text-only link button with right arrow — used for "Explore" CTAs
    ghost: 'group text-primary bg-transparent hover:text-primary-dark px-0 h-auto',
  }

  return [base, sizes[props.size], variants[props.variant]].join(' ')
})
</script>

<template>
  <component
    :is="tag"
    :class="classes"
    :href="href"
    :target="target"
    :rel="href && target === '_blank' ? 'noopener noreferrer' : rel"
    :disabled="as === 'button' && disabled ? true : undefined"
    v-bind="$attrs"
  >
    <slot />
    <ArrowRight v-if="variant === 'ghost' && icon !== false" :size="16" class="transition-transform duration-200 group-hover:translate-x-1" />
  </component>
</template>
